/**
 * What stands between the person and a video, voice or tool family: one line
 * per repository behind it, with the button its next step needs.
 *
 * The same answer the Library cards read (/api/system/readiness, entries
 * marked with their family), so Video and Audio say what the Library says --
 * the music model's licence, a first video's gigabytes -- instead of failing
 * on the machine after the click.
 */
import { APIClient } from "../api/client.js";
import { serverGenerates } from "../services/execution.js";
import { checkHuggingFaceAccess, linkHuggingFace } from "../services/hugging-face.js";
import { downloadOnMachine } from "../services/model-actions.js";
import { store } from "../state/store.js";
import type { ModelReadiness } from "../types/index.js";
import { toast } from "./Toast.js";
import { h, setChildren } from "./dom.js";
import { formatGb } from "./format.js";

export interface ReadinessPanel {
	element: HTMLElement;
	/**
	 * Show the steps for these families (keys of app/core/family_models), or
	 * none for "" / []. A server that generates itself answers for its own disk.
	 */
	show(families: string | string[]): void;
	/** Ask the server again, e.g. once the machine has reported a change. */
	refresh(): Promise<void>;
}

export function createReadinessPanel(): ReadinessPanel {
	const element = h("div", { class: "readiness-panel", hidden: true });
	let families: string[] = [];
	let answers: ModelReadiness[] = [];
	const busy = new Map<string, string>();

	function render(): void {
		const mine = answers.filter(
			(answer) => answer.family != null && families.includes(answer.family)
		);
		const pending = mine.filter((answer) => !answer.ready || busy.has(answer.model_id));
		element.hidden = pending.length === 0;
		setChildren(element, ...pending.map(line));
	}

	function line(answer: ModelReadiness): HTMLElement {
		const next = answer.next;
		const running = busy.get(answer.model_id);
		const buttons: HTMLElement[] = [];
		const downloads = !running && next?.action === "download";
		if (downloads) {
			// Worded as the Library's cards: the size once, on the button.
			const label = next?.size_gb ? `Télécharger · ${formatGb(next.size_gb, 2)}` : "Télécharger";
			buttons.push(
				button(label, async () => {
					const failed = await downloadOnMachine(
						answer.model_id,
						(text) => {
							busy.set(answer.model_id, text);
							render();
						},
						answer.family
					);
					busy.delete(answer.model_id);
					if (failed) toast(failed, "error");
					else
						toast(
							`« ${answer.name ?? answer.model_id} » est prêt sur votre ordinateur.`,
							"success"
						);
					await refresh();
				})
			);
		}
		if (!running && next?.action === "hf_connect") {
			buttons.push(
				button("Relier Hugging Face", async () => {
					if (await linkHuggingFace()) await refresh();
				})
			);
		}
		if (!running && next?.action === "accept_licence" && next.link) {
			buttons.push(
				h(
					"a",
					{ class: "btn btn-small", href: next.link, target: "_blank", rel: "noopener noreferrer" },
					"Accepter la licence ↗"
				),
				button("J'ai accepté — vérifier", async () => {
					const access = await checkHuggingFaceAccess();
					if (!access) return;
					toast(
						access[answer.model_id]
							? "Licence acceptée."
							: "Hugging Face ne voit pas encore la licence acceptée : vérifiez sur sa page, avec le même compte.",
						access[answer.model_id] ? "success" : "warning"
					);
					await refresh();
				})
			);
		}
		// The button says it all for a download; the name alone stays.
		const text = running ?? (downloads ? "" : (next?.title ?? "À vérifier"));
		return h(
			"div",
			{ class: "readiness-line" },
			h(
				"span",
				{ title: next?.detail ?? "" },
				h(
					"strong",
					null,
					text ? `${answer.name ?? answer.model_id} : ` : (answer.name ?? answer.model_id)
				),
				text
			),
			buttons.length > 0 ? h("div", { class: "readiness-actions" }, ...buttons) : null
		);
	}

	function button(label: string, run: () => Promise<void>): HTMLElement {
		const node = h(
			"button",
			{
				class: "btn btn-small",
				type: "button",
				onclick: () => {
					node.setAttribute("disabled", "");
					void run().finally(() => node.removeAttribute("disabled"));
				},
			},
			label
		);
		return node;
	}

	async function refresh(): Promise<void> {
		try {
			answers = await APIClient.getReadiness();
		} catch {
			answers = [];
		}
		render();
	}

	// A machine coming online, linking its account or receiving a model
	// changes the answer: ask again, wherever this panel is shown.
	store.subscribe(
		(s) =>
			s.workers
				.filter((worker) => worker.online)
				.map(
					(worker) =>
						`${worker.worker_id}:${worker.hf_account ?? ""}:${Object.keys(
							worker.cached_models_gb ?? {}
						)
							.sort()
							.join(",")}:${Object.keys(worker.incomplete_models ?? {})
							.sort()
							.join(",")}`
				)
				.join("|"),
		() => {
			if (families.length > 0 && !serverGenerates()) void refresh();
		}
	);

	return {
		element,
		show(next: string | string[]): void {
			const wanted = (Array.isArray(next) ? next : [next]).filter(Boolean);
			const same = wanted.join("|") === families.join("|");
			families = serverGenerates() ? [] : wanted;
			render();
			// Asked again only when the families change: the form calls this on
			// every keystroke, and the store subscription covers the rest.
			if (families.length > 0 && !same) void refresh();
		},
		refresh,
	};
}
